import {
  ChatContentResultTypenames,
  ChatEventType,
  ChatSender,
  CustomerChatMessage,
} from '@varo/graphql-schema-types';
import { TYPING_TIMEOUTS } from 'shared/constants/chat';
import { logError } from 'shared/utils/sentry/sentry';
import { UseQueryExecute } from 'urql';

export type OperationVariables = Record<string, unknown>;

//cannot localize this string, as it is a check passed to GQL
export const CHAT_START_MSG = '_start_chat_cta';

export const getIsTransient = (chatEvent: CustomerChatMessage) => {
  return [
    ChatEventType.QueuePosition,
    ChatEventType.ConnectedToAnAgent,
    ChatEventType.TransferredToAnotherAgent,
    ChatEventType.UndefinedEventType,
  ].includes(chatEvent.eventType);
};

export const getMergedChatHistory = (
  historyA: Array<CustomerChatMessage>,
  historyB: Array<CustomerChatMessage>,
): Array<CustomerChatMessage> => {
  const sortedConcatenatedHistory = [...historyA, ...historyB].sort((a, b) => {
    const timeA = new Date(a.createdAt).getTime();
    const timeB = new Date(b.createdAt).getTime();

    return timeB - timeA;
  });

  const items = new Set<string>();
  const reducedHistory = sortedConcatenatedHistory
    .map((item) => {
      if (!items.has(item.id)) {
        items.add(item.id);
        return item;
      }
      return null;
    })
    .filter((elm) => elm !== null) as CustomerChatMessage[];
  return reducedHistory;
};

export const getUserChoiceMessage = (
  chatEvent: CustomerChatMessage,
): boolean => {
  let userChoiceMessage: boolean;
  if (chatEvent.content && 'html' in chatEvent.content) {
    // chatEvent is HTML
    userChoiceMessage =
      chatEvent.sender === ChatSender.User &&
      (chatEvent?.content?.html || '').includes('parent_message_id');
  } else {
    // chatEvent is text
    userChoiceMessage =
      chatEvent.sender === ChatSender.User &&
      (
        (chatEvent?.content?.__typename ===
          ChatContentResultTypenames.ChatContentMessage &&
          chatEvent?.content.message) ||
        ''
      ).includes('parent_message_id');
  }
  return userChoiceMessage;
};

export const getHistory = (
  history: CustomerChatMessage[],
  event: CustomerChatMessage,
): CustomerChatMessage[] => {
  const isEventExists = history.find((message) => message.id === event.id);
  const updatedList = isEventExists ? [...history] : [...history, event];

  return updatedList.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
};

export const getMergedQueuePosition = (
  history: CustomerChatMessage[],
  queuePosition: CustomerChatMessage,
) => {
  const filteredHistory = history.filter(
    (event) => event.eventType !== ChatEventType.QueuePosition,
  );
  return getHistory(filteredHistory, queuePosition);
};

export interface ChatEventHandler {
  chatEvent: CustomerChatMessage;
  getAvailabilityInfo: UseQueryExecute;
  setIsAgentTyping: (msg?: boolean) => void;
  setQueuePosition: (msg?: CustomerChatMessage) => void;
  setIsSessionActive: (isSessionActive: boolean) => void;
  setIsChattingWithBot: (isChattingWithBot: boolean) => void;
  setChatSessionId: (chatSessionId: string) => void;
  startBotChat?: (sessionId?: string) => void;
  setHistory: (
    func: (history: Array<CustomerChatMessage>) => Array<CustomerChatMessage>,
  ) => void;
  setIsChatStarted: (isChatStarted: boolean) => void;
  setIsChatEndedFromEvent: (isChatEndedFromEvent: boolean) => void;
}

/**
 * Handles chat events and returns a cleanup function to clear any pending timeouts.
 * The caller MUST call the cleanup function on unmount to prevent setState after unmount.
 */
export const handleChatEvent = ({
  chatEvent: event,
  getAvailabilityInfo,
  setIsAgentTyping,
  setIsChattingWithBot,
  setChatSessionId,
  startBotChat,
  setHistory,
  setIsSessionActive,
  setQueuePosition,
  setIsChatStarted,
  setIsChatEndedFromEvent,
}: ChatEventHandler): (() => void) => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  switch (event.eventType) {
    case ChatEventType.AgentStartedTyping:
      setIsChattingWithBot(false);
      setQueuePosition(undefined);
      setIsAgentTyping(true);
      // JT-92539 There is no stop typing event in AWS Connect.
      // Remove the agent typing indicator after specific interval manually
      timeoutId = setTimeout(() => {
        setIsAgentTyping(undefined);
      }, TYPING_TIMEOUTS.agent);
      break;
    case ChatEventType.AgentStoppedTyping:
      setIsChattingWithBot(false);
      setIsAgentTyping(undefined);
      break;
    case ChatEventType.QueuePosition:
      setIsChattingWithBot(false);
      setQueuePosition(event);
      break;
    case ChatEventType.ConnectedToAnAgent:
      setIsChattingWithBot(false);
      setQueuePosition(undefined);
      setIsSessionActive(true);
      setHistory((history) => getHistory(history, event));
      break;
    case ChatEventType.ParticipantIdle:
      setHistory((history) => getHistory(history, event));
      setIsChattingWithBot(false);
      break;
    case ChatEventType.ParticipantAutoDisconnection:
      setHistory((history) => getHistory(history, event));
      setIsChattingWithBot(false);
      setChatSessionId('');
      break;
    case ChatEventType.SessionEnded:
      setIsAgentTyping(undefined);
      setQueuePosition(undefined);
      setIsSessionActive(false);
      setHistory((history) => getHistory(history, event));
      void getAvailabilityInfo();
      setIsChattingWithBot(false);
      setIsChatStarted(false);
      setIsChatEndedFromEvent(true);
      break;
    case ChatEventType.Message:
      if (event.content) {
        const skipMsg =
          event.content.__typename ===
            ChatContentResultTypenames.ChatContentHtml &&
          (event.content.html || '').indexOf(CHAT_START_MSG) >= 0;

        // JT-88362 Lex responses are recieved faster than sendMessage response
        // So history does not display in correct order
        // Hence, wait for 1sec to render the user msg first and then the Lex response
        const isBotSender = [ChatSender.LexBot, ChatSender.DecagonBot].includes(
          event.sender,
        );

        // For bot messages, set session state BEFORE adding to history.
        // This ensures getMsgState sees the correct chatSessionId when the
        // component re-renders, preventing the message from being hidden.
        // Also reset isChatEndedFromEvent since a new bot message indicates
        // a new session has started.
        if (isBotSender) {
          if (event.sessionId) {
            setChatSessionId(event.sessionId);
          }
          // Prefer semantic action when available
          if (startBotChat) {
            startBotChat(event.sessionId ?? undefined);
          } else {
            setIsChattingWithBot(true);
            setIsChatStarted(true);
            setIsChatEndedFromEvent(false);
          }
        }

        timeoutId = setTimeout(
          () => {
            // On every page visibility change, the useConversation component
            // re-render resulting in adding duplicates in history state
            if (!skipMsg) {
              setHistory((history) => getHistory(history, event));
            }
            // once an agent sends a message there's a delay on the receipt of
            // the `AgentStoppedTyping` event
            setIsAgentTyping(undefined);
          },
          isBotSender ? 1000 : 0,
        );
      }
      break;
    default:
      logError(new Error(`Unhandled ChatEventType:${event.eventType}`));
  }

  // Return cleanup function to clear pending timeout
  return () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };
};

export const getMsgState = (
  item: CustomerChatMessage,
  index: number,
  chatSessionId: string,
  isChattingWithBot: boolean,
) => {
  if (item.sessionId !== chatSessionId) {
    return true;
  }

  if (!isChattingWithBot) {
    return ![ChatSender.Agent, ChatSender.User].includes(item.sender);
  }

  return (
    index !== 0 &&
    item.content?.__typename ===
      ChatContentResultTypenames.ChatContentLexBotOption
  );
};
